/**
 * EditorThroughputTab — Tank Redesign Statistics primary surface.
 * Source: design_handoff_tank/README.md § Statistics.
 *
 * Layout:
 *   1. KPI row (4 tiles): Photos this week / Avg edit time / Error rate / Active editors
 *   2. Throughput sparkline with 7d / 30d / 90d range selector
 *   3. Status mix bar (stacked) for the current week + legend
 *   4. Leaderboard (per-editor: throughput · errors · avg edit time)
 *
 * Data shape comes from `derivedStats` + `metricsStats` + `editorSendsStats`.
 * Numbers that aren't yet exposed by the backend render as `—` so the UI
 * can ship before the API endpoint catches up. The shell honours the
 * canonical 6-state palette via the existing `--rd-status-*` tokens.
 */

import { useMemo } from 'react';
import Sparkline from './Sparkline';
import { formatMs } from '../utils';
import styles from './EditorThroughputTab.module.css';

// Range labels for the sparkline section header. The actual range
// selector lives at the page level (Statistics.jsx) — this component
// only consumes the resolved `range` prop.
const RANGE_LABELS = {
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  '1y': 'Last year',
  all: 'All time',
};

const STATUS_KINDS = ['pending', 'attention', 'error', 'done', 'exported', 'skipped'];

const STATUS_LABELS = {
  pending: 'Pending',
  attention: 'Attention',
  error: 'Error',
  done: 'Done',
  exported: 'Exported',
  skipped: 'Skipped',
};

function KpiTile({ label, value, hint, delta }) {
  // delta: { value: number, direction: 'up' | 'down', good: boolean }
  // good=true means up is positive; good=false means down is positive
  // (e.g. error rate: down is good).
  const showDelta = delta && Number.isFinite(delta.value);
  const isPositive = showDelta && (delta.direction === 'up' ? delta.good : !delta.good);
  return (
    <div className={styles.kpiTile}>
      <div className={styles.kpiLabel}>{label}</div>
      <div className={styles.kpiValue}>{value ?? '—'}</div>
      <div className={styles.kpiSubline}>
        {showDelta && (
          <span
            className={`${styles.kpiDelta} ${
              isPositive ? styles.kpiDeltaPositive : styles.kpiDeltaNegative
            }`}
          >
            {delta.direction === 'up' ? '+' : '−'}
            {Math.abs(delta.value)}%
          </span>
        )}
        {hint ? <span className={styles.kpiHint}>{hint}</span> : null}
      </div>
    </div>
  );
}

function StatusMixBar({ counts }) {
  const total = STATUS_KINDS.reduce((acc, k) => acc + (counts[k] || 0), 0);
  if (!total) {
    return <div className={styles.statusMixEmpty}>No photos in selected range</div>;
  }
  return (
    <div className={styles.statusMixBar} role="img" aria-label="Status mix">
      {STATUS_KINDS.map((k) => {
        const c = counts[k] || 0;
        if (!c) return null;
        const pct = (c / total) * 100;
        return (
          <span
            key={k}
            className={`${styles.statusSegment} ${styles[`segment_${k}`]}`}
            style={{ width: `${pct}%` }}
            title={`${STATUS_LABELS[k]} · ${c} (${pct.toFixed(1)}%)`}
          />
        );
      })}
    </div>
  );
}

function StatusMixLegend({ counts }) {
  const total = STATUS_KINDS.reduce((acc, k) => acc + (counts[k] || 0), 0);
  return (
    <div className={styles.statusLegend}>
      {STATUS_KINDS.map((k) => {
        const c = counts[k] || 0;
        const pct = total ? ((c / total) * 100).toFixed(0) : 0;
        return (
          <div key={k} className={styles.legendItem}>
            <span className={`${styles.legendSwatch} ${styles[`segment_${k}`]}`} />
            <span className={styles.legendLabel}>{STATUS_LABELS[k]}</span>
            <span className={styles.legendValue}>{c}</span>
            <span className={styles.legendPct}>{pct}%</span>
          </div>
        );
      })}
    </div>
  );
}

function Leaderboard({ rows }) {
  if (!rows?.length) {
    return <div className={styles.leaderboardEmpty}>Editor breakdown not available</div>;
  }
  return (
    <table className={styles.leaderboard}>
      <thead>
        <tr>
          <th>Editor</th>
          <th>Throughput</th>
          <th>Errors</th>
          <th>Avg edit</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.editor}>
            <td className={styles.leaderName}>{r.editor}</td>
            <td className={styles.leaderNum}>{r.throughput}</td>
            <td className={styles.leaderNum}>{r.errors}</td>
            <td className={styles.leaderNum}>{r.avg_edit_ms ? formatMs(r.avg_edit_ms) : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function EditorThroughputTab({ range, overview, rate }) {
  // All section data comes from the page-level `overview` payload (one
  // /api/stats/overview/?range=X fetch in Statistics.jsx). The range
  // selector lives at the page header — we just consume the value.

  const kpis = overview?.kpis || {};
  const sparklineData = overview?.daily_throughput || [];
  const statusCounts = useMemo(
    () => ({
      pending: overview?.status_mix?.pending || 0,
      attention: overview?.status_mix?.attention || 0,
      error: overview?.status_mix?.error || 0,
      done: overview?.status_mix?.done || 0,
      exported: overview?.status_mix?.exported || 0,
      skipped: overview?.status_mix?.skipped || 0,
    }),
    [overview],
  );

  // Error rate = attention / (done + exported + attention) within the
  // range. Computed client-side from the status mix.
  const errorDenom =
    (statusCounts.done || 0) + (statusCounts.exported || 0) + (statusCounts.attention || 0);
  const errorRate =
    errorDenom > 0 ? `${((statusCounts.attention / errorDenom) * 100).toFixed(1)}%` : null;

  const rangeLabel = RANGE_LABELS[range] || RANGE_LABELS["30d"];

  return (
    <div className={styles.tab}>
      {/* KPI tiles — 5 per the handoff: Photos ingested · Avg edit time ·
          Throughput · Error rate · Exports. Range-aware via overview. */}
      <div className={styles.kpiRow}>
        <KpiTile
          label="Photos ingested"
          value={kpis.total_images?.toLocaleString() || null}
        />
        <KpiTile
          label="Avg edit time"
          value={kpis.avg_pipeline_seconds ? `${kpis.avg_pipeline_seconds.toFixed(2)}s` : null}
        />
        <KpiTile
          label="Throughput"
          value={
            kpis.images_per_min
              ? `${Number(kpis.images_per_min).toFixed(2)} / min`
              : rate?.per_min_5m
                ? `${rate.per_min_5m.toFixed(1)} / min`
                : null
          }
        />
        <KpiTile label="Error rate" value={errorRate} />
        <KpiTile
          label="Exports"
          value={statusCounts.exported?.toLocaleString() || null}
        />
      </div>

      {/* Throughput sparkline. Range selector lives at the page header
          (Statistics.jsx) so all tabs share one window. */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Throughput · {rangeLabel.toLowerCase()}</h3>
        </div>
        <div className={styles.sparklineWrap}>
          <Sparkline
            data={sparklineData}
            width={760}
            height={120}
            color="var(--rd-color-accent)"
            strokeWidth={2}
          />
          {!sparklineData.length && (
            <div className={styles.sparklineEmpty}>No data for selected range</div>
          )}
        </div>
      </div>

      {/* Status mix — label tracks the page-level range. */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Status mix · {rangeLabel.toLowerCase()}</h3>
        </div>
        <StatusMixBar counts={statusCounts} />
        <StatusMixLegend counts={statusCounts} />
      </div>

      {/* Leaderboard — backend doesn't yet return per-editor stats from
          the overview endpoint. Renders its own empty state. */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Leaderboard</h3>
        </div>
        <Leaderboard rows={[]} />
      </div>
    </div>
  );
}
